import { useMemo, useRef, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { apiClient } from '../services/api';

interface HubSpotRouteProps {
  children: ReactNode;
}

/**
 * Chrome-less wrapper for routes rendered inside a HubSpot iframe modal.
 *
 * - Reads `token` from URL search params (?token=xyz)
 * - Installs the token getter on apiClient SYNCHRONOUSLY during render so the
 *   Authorization header is present on the very first request from a child's
 *   useQuery(). Deferring this to useEffect caused a race in React 18 Strict
 *   Mode (double-invoke) where children's queries could fire before, between,
 *   or after cleanup and miss the header.
 * - Intentionally does NOT clear the getter on unmount. Strict Mode's
 *   double-invoke would run the cleanup after install and clobber the token
 *   before children's effects dispatched their queries. Inside an iframe
 *   modal the whole window unloads on close, so leaking the getter at
 *   "unmount" is a non-issue.
 * - Skips Descope auth entirely. Renders without sidebar/navigation.
 *
 * TODO(two-step-fetch): When migrating to HubSpot's two-step token fetch
 * pattern, this component stays identical — only the token issuance endpoint
 * name changes (see backend/api/hubspot_iframe_auth.py).
 */
const HubSpotRoute: React.FC<HubSpotRouteProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Use a ref to hold the current token so the getter is stable across
  // renders and always reflects the latest token. Installing the getter
  // synchronously (outside useEffect) ensures it's live before any child
  // mounts and issues its first API call.
  const tokenRef = useRef<string | null>(token);
  tokenRef.current = token;

  // Install a stable getter once per mount. setTokenGetter stores the
  // callback; on every outbound request api.ts's interceptor calls it and
  // reads tokenRef.current, which we keep in sync above.
  if (token && apiClient) {
    apiClient.setTokenGetter(() => tokenRef.current ?? undefined);
    console.log(
      `[HubSpotRoute] setTokenGetter installed during render. token=${token.substring(0, 20)}...(${token.length} chars)`,
    );
  } else {
    console.log(`[HubSpotRoute] NOT installing getter. token=${token ? 'present' : 'null'}, apiClient=${!!apiClient}`);
  }

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 30000,
          },
        },
      }),
    [],
  );

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <p className="text-destructive font-medium">Missing authentication token</p>
          <p className="text-sm text-muted-foreground mt-1">
            This page must be opened from HubSpot.
          </p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="bg-background min-h-screen">
        {children}
      </div>
    </QueryClientProvider>
  );
};

export default HubSpotRoute;
